import React from "react";
import { useTranslation } from "react-i18next";
import InfoSection from "@quest_editor/components/InfoSection";
import { JoinConditionValue } from "@quest_editor/constants";
import {
  Store,
  ProductWithManufacturer,
  Category,
  Manufacturer,
  Keyword,
  PurchaseChannel,
} from "@quest_editor/mocks";
import PurchaseChannelField from "@quest_editor/components/QuestCondition/PurchaseChannelField";
import TargetStoreField from "@quest_editor/components/QuestCondition/TargetStoreField";
import TargetProductField from "@quest_editor/components/QuestCondition/TargetProductField";
import CategoryField from "@quest_editor/components/QuestCondition/CategoryField";
import ManufacturerField from "@quest_editor/components/QuestCondition/ManufacturerField";
import KeywordField from "@quest_editor/components/QuestCondition/KeywordField";
import ElapsedDaysField from "./ElapsedDaysField";
import JoinConditionField from "./JoinConditionField";
import PurchaseDateField from "./PurchaseDateField";
import PurchaseFrequencyField from "./PurchaseFrequencyField";

const PurchaseSection = ({
  elapsedDays,
  setElapsedDays,
  joinCondition,
  setJoinCondition,
  purchaseChannels,
  purchaseChannelIds,
  setPurchaseChannelIds,
  targetStoreRows,
  setTargetStoreRows,
  targetProductRows,
  setTargetProductRows,
  categoryRows,
  setCategoryRows,
  manufacturerRows,
  setManufacturerRows,
  keywordRows,
  setKeywordRows,
  purchaseDateFrom,
  setPurchaseDateFrom,
  purchaseDateTo,
  setPurchaseDateTo,
  purchaseFrequency,
  setPurchaseFrequency,
}: {
  elapsedDays: string;
  setElapsedDays: React.Dispatch<React.SetStateAction<string>>;
  joinCondition: JoinConditionValue;
  setJoinCondition: React.Dispatch<React.SetStateAction<JoinConditionValue>>;
  purchaseChannels: PurchaseChannel[];
  purchaseChannelIds: string[];
  setPurchaseChannelIds: React.Dispatch<React.SetStateAction<string[]>>;
  targetStoreRows: Store[];
  setTargetStoreRows: React.Dispatch<React.SetStateAction<Store[]>>;
  targetProductRows: ProductWithManufacturer[];
  setTargetProductRows: React.Dispatch<
    React.SetStateAction<ProductWithManufacturer[]>
  >;
  categoryRows: Category[];
  setCategoryRows: React.Dispatch<React.SetStateAction<Category[]>>;
  manufacturerRows: Manufacturer[];
  setManufacturerRows: React.Dispatch<React.SetStateAction<Manufacturer[]>>;
  keywordRows: Keyword[];
  setKeywordRows: React.Dispatch<React.SetStateAction<Keyword[]>>;
  purchaseDateFrom: string;
  setPurchaseDateFrom: React.Dispatch<React.SetStateAction<string>>;
  purchaseDateTo: string;
  setPurchaseDateTo: React.Dispatch<React.SetStateAction<string>>;
  purchaseFrequency: number;
  setPurchaseFrequency: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation("questEditor");

  return (
    <InfoSection
      title={t("quest_edit_target.section.purchase")}
      rows={[
        {
          label: t("quest_edit_target.field.purchase.elapsed_days.name"),
          children: (
            <ElapsedDaysField
              elapsedDays={elapsedDays}
              setElapsedDays={setElapsedDays}
            />
          ),
        },
        {
          label: t("quest_edit_target.field.purchase.content.name"),
          extra: (
            <JoinConditionField
              joinCondition={joinCondition}
              setJoinCondition={setJoinCondition}
            />
          ),
          children: (
            <InfoSection
              rows={[
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_purchase_channel.name",
                  ),
                  children: (
                    <PurchaseChannelField
                      purchaseChannels={purchaseChannels}
                      purchaseChannelIds={purchaseChannelIds}
                      setPurchaseChannelIds={setPurchaseChannelIds}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.all_target",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_target_store.name",
                  ),
                  children: (
                    <TargetStoreField
                      targetStoreRows={targetStoreRows}
                      setTargetStoreRows={setTargetStoreRows}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.all_target",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_target_product.name",
                  ),
                  children: (
                    <TargetProductField
                      targetProductRows={targetProductRows}
                      setTargetProductRows={setTargetProductRows}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.all_target",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_category.name",
                  ),
                  children: (
                    <CategoryField
                      categoryRows={categoryRows}
                      setCategoryRows={setCategoryRows}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.all_target",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_manufacturer.name",
                  ),
                  children: (
                    <ManufacturerField
                      manufacturerRows={manufacturerRows}
                      setManufacturerRows={setManufacturerRows}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.all_target",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_keyword.name",
                  ),
                  children: (
                    <KeywordField
                      keywordRows={keywordRows}
                      setKeywordRows={setKeywordRows}
                      emptyLabel={t(
                        "quest_edit_target.label.not_specified.none",
                      )}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_purchase_date.name",
                  ),
                  children: (
                    <PurchaseDateField
                      purchaseDateFrom={purchaseDateFrom}
                      setPurchaseDateFrom={setPurchaseDateFrom}
                      purchaseDateTo={purchaseDateTo}
                      setPurchaseDateTo={setPurchaseDateTo}
                    />
                  ),
                },
                {
                  label: t(
                    "quest_edit_target.field.purchase.content.by_purchase_frequency.name",
                  ),
                  children: (
                    <PurchaseFrequencyField
                      purchaseFrequency={purchaseFrequency}
                      setPurchaseFrequency={setPurchaseFrequency}
                    />
                  ),
                },
              ]}
            />
          ),
        },
      ]}
    />
  );
};

export default PurchaseSection;
